import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";

/** Fetches the owned space ID for a given user. Used in onboarding to scope child creation. */
export async function fetchOwnerSpace(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from("spaces")
    .select("id")
    .eq("owner_id", userId)
    .is("deleted_at", null)
    .maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

/** For co-parents/members: fetches the space owner's nickname from the space_members join. */
export async function fetchCoParentOwnerNickname(userId: string): Promise<string | null> {
  const { data: membership } = await supabase
    .from("space_members")
    .select("spaces:spaces!space_id(owner:profiles!owner_id(nickname))")
    .eq("user_id", userId)
    .eq("status", "active")
    .neq("role", "owner")
    .limit(1)
    .maybeSingle();
  return (
    (membership as { spaces: { owner: { nickname: string } | null } | null } | null)
      ?.spaces?.owner?.nickname ?? null
  );
}

export function invalidateOwnedSpaceSettings(userId: string) {
  return queryClient.invalidateQueries({ queryKey: ["owned-space-settings", userId] });
}

/** Fetches the owned space ID + AI toggle for the Profile page AI prefs row. */
export function useOwnedSpaceSettings(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["owned-space-settings", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("spaces")
        .select("id, ai_processing_enabled")
        .eq("owner_id", userId!)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      const row = data as { id: string; ai_processing_enabled: boolean } | null;
      return row ? { spaceId: row.id, aiEnabled: row.ai_processing_enabled } : null;
    },
  });

  return {
    data: query.data ?? null,
    spaceId: query.data?.spaceId ?? null,
    aiEnabled: query.data?.aiEnabled ?? true,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
